import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { PriorOpenMonth } from '../../services/invoiceLogic';
import { formatCents } from '../../services/money';
import type { BillingMode } from '../../services/billingMode';
import {
    checkAdvanceAmount,
    readAdvanceAmount,
    type AdvanceAmountChoice,
} from '../../services/invoicePickerFlow';
import { monthLabel } from './invoicesViewShared';
import { useStepFocus } from './useStepFocus';

/**
 * „Schritt 2 von 3 · Abrechnungsart" ueber jedem Schritt der Rueckfrage-Kette.
 *
 * WARUM: nach dem Klick auf ein Projekt koennen bis zu fuenf Fragen kommen.
 * Als fuenf voneinander unabhaengige Fenster waere das eine Folge von
 * Ueberraschungen — man weiss weder, wie viele noch kommen, noch dass man
 * zurueck kann. Die Anzeige ist bewusst dieselbe Zeile in allen Schritten,
 * auch in denen, die eine eigene Komponente haben.
 *
 * `total` kann nach der Antwort auf Schritt 1 SINKEN: ein kumulativer Beleg
 * laesst Abschlagsabzug und Vormonate wegfallen. Geplant wird deshalb mit der
 * groesseren Kette (invoicePickerFlow.ts) — sie kann schrumpfen, nie wachsen.
 */
export const PickerStepBadge: React.FC<{
    projectName: string;
    step: { position: number; total: number };
}> = ({ projectName, step }) => (
    <p className="mb-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
        {step.total > 1 && step.position > 0 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">
                Schritt {step.position} von {step.total}
            </span>
        )}
        <span className="truncate">{projectName}</span>
    </p>
);

/**
 * Rueckfrage vor dem Anlegen: vor dem gewaehlten Zeitraum liegt noch Offenes.
 *
 * Der Zeitraumfilter laesst diese Monate stillschweigend liegen. Die Rechnung
 * entsteht, sieht vollstaendig aus, und der Altbestand faellt erst Monate
 * spaeter auf — deshalb diese Zwischenfrage statt eines Hinweistextes.
 *
 * Die Auswahl ist bewusst KEIN freies Ankreuzen: der Leistungszeitraum auf dem
 * Beleg ist eine zusammenhaengende Spanne (von–bis). Zwei Monate mit einer
 * Luecke dazwischen liessen sich dort nicht abbilden, ohne den Beleg falsch zu
 * beschriften. Modelliert ist deshalb "einbeziehen ab Monat X": ein Haken
 * setzt alle spaeteren mit, ein entfernter Haken nimmt alle frueheren heraus.
 *
 * Bewusst ohne Effekt zum Zuruecksetzen — die Komponente wird nur montiert,
 * solange die Frage offen ist, und startet damit bei jedem Oeffnen frisch.
 */
export const PriorMonthsDialog: React.FC<{
    projectName: string;
    /** Aufsteigend sortiert; die Reihenfolge traegt hier die Auswahllogik. */
    months: PriorOpenMonth[];
    /** „Schritt 2 von 3" — dieselbe Anzeige wie in den uebrigen Schritten. */
    step: { position: number; total: number };
    /** Fehlt, wenn dies der erste Schritt der Kette ist. */
    onBack?: () => void;
    onCancel: () => void;
    onSkip: () => void;
    /** 'JJJJ-MM' des fruehesten einbezogenen Monats. */
    onInclude: (fromMonth: string) => void;
}> = ({ projectName, months, step, onBack, onCancel, onSkip, onInclude }) => {
    const panelRef = useStepFocus();
    // Voreinstellung: alles einbeziehen. Wer den Altbestand stehen lassen will,
    // sagt das ausdruecklich — nicht umgekehrt.
    const [includeFrom, setIncludeFrom] = useState<string | null>(months[0]?.month ?? null);

    const selected = (month: string) => includeFrom != null && month >= includeFrom;
    const selectedMonths = months.filter((m) => selected(m.month));
    const selectedCents = selectedMonths.reduce((sum, m) => sum + m.netCents, 0);
    const totalCents = months.reduce((sum, m) => sum + m.netCents, 0);

    /**
     * Ein Haken schiebt die Untergrenze, er setzt keinen einzelnen Monat.
     *
     * Ankreuzen: ab hier alles. Abwaehlen: erst ab dem naechsten Monat — der
     * abgewaehlte und alles davor fallen heraus. So bleibt die Auswahl immer
     * ein Bereich bis zum letzten Vormonat.
     */
    const toggle = (index: number) => {
        const month = months[index].month;
        if (selected(month)) setIncludeFrom(months[index + 1]?.month ?? null);
        else setIncludeFrom(month);
    };

    // z-[110]: eine Stufe ueber der Modalebene z-[100]. Der Ruecklauf kommt aus
    // dem Anlagedialog und muss auf ihm liegen, nicht unter ihm.
    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 animate-fade-in backdrop-blur-sm p-4">
            <div
                ref={panelRef}
                tabIndex={-1}
                className="max-h-[80vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl outline-none"
            >
                <div className="flex items-start gap-2 border-b border-slate-200 px-5 py-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <div className="min-w-0">
                        <PickerStepBadge projectName={projectName} step={step} />
                        <h2 className="font-semibold text-slate-800">
                            Offene Summen aus Vormonaten
                        </h2>
                    </div>
                </div>

                <div className="max-h-[50vh] overflow-y-auto px-5 py-4">
                    <p className="text-sm leading-snug text-slate-600">
                        Vor dem gewählten Abrechnungszeitraum sind noch{' '}
                        <strong className="tabular-nums">{formatCents(totalCents)}</strong> offen.
                        Diese Mengen stehen <em>nicht</em> auf der Rechnung, wenn du den Zeitraum so
                        lässt.
                    </p>
                    <p className="mt-2 text-xs leading-snug text-slate-500">
                        Der Leistungszeitraum auf dem Beleg ist eine zusammenhängende Spanne. Ein
                        Monat lässt sich deshalb nur zusammen mit allen späteren einbeziehen — der
                        Zeitraum beginnt dann am Ersten des frühesten angehakten Monats.
                    </p>

                    <ul className="mt-3 space-y-1">
                        {months.map((bucket, index) => {
                            const on = selected(bucket.month);
                            return (
                                <li key={bucket.month}>
                                    <label
                                        className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ring-1 ring-inset ${
                                            on
                                                ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
                                                : 'bg-slate-50 text-slate-500 ring-slate-200'
                                        }`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={on}
                                                onChange={() => toggle(index)}
                                            />
                                            <span>{monthLabel(bucket.month)}</span>
                                        </span>
                                        <span className="whitespace-nowrap font-semibold tabular-nums">
                                            {formatCents(bucket.netCents)}
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className="mr-auto rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                        >
                            ← Zurück
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                    >
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={onSkip}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-400"
                    >
                        Ohne Vormonate fortfahren
                    </button>
                    <button
                        type="button"
                        disabled={includeFrom == null}
                        onClick={() => includeFrom && onInclude(includeFrom)}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                        Auswahl einbeziehen
                        {includeFrom != null && (
                            <span className="ml-1 font-normal tabular-nums">
                                ({formatCents(selectedCents)})
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Der gemeinsame Rahmen der Rueckfragen nach dem Klick auf ein Projekt.
 *
 * WARUM ein Rahmen und nicht drei Dialoge: der Anlagedialog stellt bis zu fuenf
 * Fragen hintereinander. Ohne gemeinsamen Rahmen waere das eine Folge fremder
 * Fenster — jedes mit eigener Kopfzeile, eigenem Abbrechen, keines mit einem
 * Weg zurueck. Hier steht immer dasselbe: wo man ist, um welches Projekt es
 * geht, und die beiden Wege heraus.
 *
 * Geschrieben wird in keinem dieser Schritte. Abbrechen laesst deshalb nichts
 * zurueck — der Beleg entsteht erst, wenn die Kette durch ist.
 *
 * z-[110]: eine Stufe ueber der Modalebene z-[100]. Die Rueckfrage kommt aus
 * dem Anlagedialog und muss auf ihm liegen, nicht unter ihm.
 */
const PickerStepFrame: React.FC<{
    projectName: string;
    title: string;
    step: { position: number; total: number };
    onBack?: () => void;
    onCancel: () => void;
    /** Die Hauptaktion(en) des Schritts, rechts in der Fusszeile. */
    actions?: React.ReactNode;
    /**
     * Den Anfangsfokus auf den Rahmen legen. `false` nur dort, wo der Schritt
     * ein Eingabefeld hat, das ihn selbst beansprucht (siehe useStepFocus).
     */
    focusPanel?: boolean;
    children: React.ReactNode;
}> = ({ projectName, title, step, onBack, onCancel, actions, focusPanel = true, children }) => {
    const panelRef = useStepFocus(focusPanel);
    return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4 animate-fade-in backdrop-blur-sm">
        <div
            ref={panelRef}
            tabIndex={-1}
            className="max-h-[85vh] w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-xl outline-none"
        >
            <div className="border-b border-slate-200 px-5 py-3">
                <PickerStepBadge projectName={projectName} step={step} />
                <h2 className="font-semibold text-slate-800">{title}</h2>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">{children}</div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
                {onBack && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="mr-auto rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                    >
                        ← Zurück
                    </button>
                )}
                <button
                    type="button"
                    onClick={onCancel}
                    className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                >
                    Abbrechen
                </button>
                {actions}
            </div>
        </div>
    </div>
    );
};

/** Eine Antwortkarte: Ueberschrift, Erklaerung, ein Klick. */
const PickerChoiceCard: React.FC<{
    title: string;
    badge?: string;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ title, badge, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex w-full flex-col items-start gap-1 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-emerald-400 hover:bg-emerald-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
    >
        <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{title}</span>
            {badge && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {badge}
                </span>
            )}
        </span>
        <span className="text-xs leading-relaxed text-slate-600">{children}</span>
    </button>
);

/**
 * Schritt „Abrechnungsart" — Zustand (c), also nur beim ersten Beleg eines
 * Projekts.
 *
 * WARUM hier und nicht in der Kopfleiste des Dialogs: dort stand sie VOR der
 * Wahl des Projekts und war bei jedem Projekt mit Abrechnungshistorie ohne
 * Wirkung — das Projektmodell ueberstimmt sie (billingMode.ts).
 *
 * WARUM nicht als Umschalter in der Projektzeile: der Zeilen-Hauptknopf ist ein
 * <button>. Ein Schalter darin waere ungueltiges HTML, weder anklickbar noch
 * vorlesbar, und die Pfeiltastensteuerung der Liste (movePickerFocus) findet
 * ausschliesslich `button[data-picker-row]`.
 *
 * Bewusst OHNE Vorauswahl: die Voreinstellung des Betriebs ist nur markiert.
 * Eine projektuebergreifend gemerkte Wahl war die Ursache von Vorfall 6172, und
 * ein vorausgewaehlter Knopf, den man mit der Eingabetaste bestaetigt, ist
 * dasselbe in langsam.
 */
export const BillingModeStep: React.FC<{
    projectName: string;
    /** „Rechnung", „Schlussrechnung", … — der Beleg, um den es geht. */
    docTypeLabel: string;
    step: { position: number; total: number };
    /** Die Regel des Betriebs aus den Einstellungen. Nur markiert, nicht gesetzt. */
    suggested: BillingMode;
    /**
     * Die externen Alt-Abschlaege konnten nicht geladen werden. Dann ist
     * ungeprueft, ob dieses Projekt Altfakturierung ohne Deckung traegt — und
     * genau die ist der Grund, aus dem „kumulativ" gesperrt waere.
     */
    advancesUnknown: boolean;
    onCancel: () => void;
    onChoose: (mode: BillingMode) => void;
}> = ({ projectName, docTypeLabel, step, suggested, advancesUnknown, onCancel, onChoose }) => (
    <PickerStepFrame
        projectName={projectName}
        title="Wie soll dieses Projekt abgerechnet werden?"
        step={step}
        onCancel={onCancel}
    >
        <p className="text-sm leading-snug text-slate-600">
            Für dieses Projekt ist noch kein Beleg ausgestellt, der eine Abrechnungsart festlegt.
            Diese {docTypeLabel} begründet sie — alle weiteren Belege des Projekts schließen daran
            an.
        </p>

        <div className="mt-3 space-y-2">
            <PickerChoiceCard
                title="Kumulativ abrechnen"
                badge={suggested === 'CUMULATIVE' ? 'Voreinstellung des Betriebs' : undefined}
                onClick={() => onChoose('CUMULATIVE')}
            >
                Die Positionen zeigen den <em>gesamten</em> Leistungsstand bis zum Ende des
                Leistungszeitraums, und abgezogen wird alles, was die vorherigen Rechnungen dieses
                Projekts bereits gefordert haben. Ergibt „Gesamtleistung − bisher abgerechnet =
                Rechnungssumme".
            </PickerChoiceCard>
            <PickerChoiceCard
                title="Fortschreibend abrechnen"
                badge={suggested === 'PROGRESSIVE' ? 'Voreinstellung des Betriebs' : undefined}
                onClick={() => onChoose('PROGRESSIVE')}
            >
                Der Beleg enthält nur die neuen Mengen des Leistungszeitraums, ohne Abzug der
                Vorrechnungen.
            </PickerChoiceCard>
        </div>

        <p className="mt-3 text-xs leading-snug text-slate-500">
            Beide Wege ergeben denselben Zahlbetrag, aber ein völlig anderes Dokument.
        </p>

        {advancesUnknown && (
            <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                    Die erfassten Alt-Abschläge konnten nicht geladen werden. Trägt dieses Projekt
                    Abrechnung aus einem Altsystem, ist „kumulativ" hier ungeprüft — der Beleg läuft
                    dann beim Ausstellen in die Sperre.
                </span>
            </p>
        )}
    </PickerStepFrame>
);

/**
 * Schritt „Höhe des Abschlags".
 *
 * WARUM nach dem Klick: die Bemessungsgrundlage ist der Auftragswert DES
 * GEWAEHLTEN PROJEKTS. In der linken Spalte stand die Eingabe, bevor dieser
 * Wert bekannt war — der Nutzer tippte einen Prozentsatz ins Blaue und erfuhr
 * den Betrag erst im fertigen Beleg. Jetzt steht die Grundlage daneben und die
 * Vorschau darunter.
 *
 * Der Entwurf der Eingaben liegt bewusst NICHT hier, sondern in der Ansicht:
 * wer zurueckgeht und wiederkommt, findet seine Zahlen wieder. Eine
 * komponenteneigene Zustandsvariable waere beim Aus- und Einhaengen weg.
 */
export const AdvanceAmountStep: React.FC<{
    projectName: string;
    step: { position: number; total: number };
    /** Auftragswert netto in Cent — die Bemessungsgrundlage. */
    basisNetCents: number;
    choice: AdvanceAmountChoice;
    onChange: (choice: AdvanceAmountChoice) => void;
    onBack?: () => void;
    onCancel: () => void;
    onSubmit: () => void;
}> = ({ projectName, step, basisNetCents, choice, onChange, onBack, onCancel, onSubmit }) => {
    const read = readAdvanceAmount(choice);
    // Vorschau UND Schranke aus derselben Rechnung (invoicePickerFlow): die
    // Zahl, die hier steht, ist die, die der Beleg fordert.
    const check = read.result ? checkAdvanceAmount(read.result, basisNetCents) : null;
    const noBasis = basisNetCents <= 0;
    /**
     * Die Bestaetigung fuer einen Abschlag ueber dem Auftragswert.
     *
     * Bewusst ein Haken und keine Sperre: das Leistungsverzeichnis kann die
     * bereits vereinbarten Nachtraege noch nicht tragen, und dann liegt der
     * vereinbarte Abschlag rechtmaessig darueber. Bewusst auch keine blosse
     * Warnung: die klickt man weg, ohne sie gelesen zu haben.
     *
     * Gemerkt wird der bestaetigte BETRAG, nicht ein Ja: wer nach der
     * Bestaetigung die Hoehe noch einmal aendert, bestaetigt einen anderen
     * Beleg — und muss erneut hinsehen. Zustand der Komponente und nicht der
     * Kette; ein Zurueck und Wiederkommen setzt ihn ebenfalls zurueck.
     */
    const [acceptedAmountCents, setAcceptedAmountCents] = useState<number | null>(null);
    const overBasisAccepted =
        check != null && acceptedAmountCents === check.amountNetCents;
    const exceeds = check?.exceedsBasis === true;
    const blocked = read.result == null || noBasis || (exceeds && !overBasisAccepted);

    return (
        <PickerStepFrame
            projectName={projectName}
            title="Höhe des Abschlags"
            step={step}
            onBack={onBack}
            onCancel={onCancel}
            // Der Fokus gehoert in das Feld, das gleich getippt wird — hier
            // steht keine Antwortkarte, die eine Eingabetaste ausloesen
            // koennte. Ohne dies bliebe das `autoFocus` unten wirkungslos.
            // Ohne Bemessungsgrundlage gibt es kein Feld, dann bekommt der
            // Rahmen den Fokus wie ueberall sonst.
            focusPanel={noBasis}
            actions={
                <button
                    type="button"
                    disabled={blocked}
                    onClick={onSubmit}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                    Abschlag anlegen
                </button>
            }
        >
            {noBasis ? (
                <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm leading-snug text-red-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        Für dieses Projekt ist kein Auftragswert hinterlegt. Ein Abschlag braucht
                        eine Bemessungsgrundlage — bitte im Leistungsverzeichnis die beauftragten
                        Mengen und Einzelpreise erfassen.
                    </span>
                </p>
            ) : (
                <>
                    <p className="text-sm leading-snug text-slate-600">
                        Bemessungsgrundlage ist der Auftragswert dieses Projekts (beauftragte Menge
                        × Einzelpreis):{' '}
                        <strong className="tabular-nums">{formatCents(basisNetCents)}</strong> netto.
                        Der Abzug auf der Schlussrechnung entsteht später automatisch.
                    </p>

                    <div className="mt-3 flex flex-wrap items-end gap-3">
                        <label className="text-sm">
                            <span className="mb-1 block text-xs font-bold uppercase text-slate-500">
                                Höhe
                            </span>
                            <select
                                value={choice.kind}
                                onChange={(e) =>
                                    onChange({
                                        ...choice,
                                        kind: e.target.value as 'percent' | 'fixed',
                                    })
                                }
                                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                            >
                                <option value="percent">Prozent vom Auftrag</option>
                                <option value="fixed">Fester Betrag</option>
                            </select>
                        </label>
                        {choice.kind === 'percent' ? (
                            <label className="text-sm">
                                <span className="mb-1 block text-xs font-bold uppercase text-slate-500">
                                    Prozentsatz
                                </span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoFocus
                                    value={choice.percent}
                                    onChange={(e) =>
                                        onChange({ ...choice, percent: e.target.value })
                                    }
                                    className="w-24 rounded-lg border border-slate-300 px-2.5 py-1.5 text-right text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                                />
                            </label>
                        ) : (
                            <label className="text-sm">
                                <span className="mb-1 block text-xs font-bold uppercase text-slate-500">
                                    Netto in €
                                </span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    autoFocus
                                    placeholder="z.B. 2500,00"
                                    value={choice.fixed}
                                    onChange={(e) => onChange({ ...choice, fixed: e.target.value })}
                                    className="w-32 rounded-lg border border-slate-300 px-2.5 py-1.5 text-right text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                                />
                            </label>
                        )}
                    </div>

                    {read.error ? (
                        <p className="mt-3 text-xs leading-snug text-red-700">{read.error}</p>
                    ) : exceeds ? (
                        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-900">
                            <p className="flex items-start gap-2">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>
                                    Der Abschlag fordert{' '}
                                    <strong className="tabular-nums">
                                        {formatCents(check?.amountNetCents ?? 0)}
                                    </strong>{' '}
                                    netto und liegt damit <strong>über</strong> dem Auftragswert von{' '}
                                    <span className="tabular-nums">
                                        {formatCents(basisNetCents)}
                                    </span>
                                    . Das ist zulässig, wenn das Leistungsverzeichnis die bereits
                                    vereinbarten Nachträge noch nicht trägt — sonst ist es ein
                                    Zahlendreher.
                                </span>
                            </p>
                            <label className="mt-2 flex cursor-pointer items-start gap-2.5">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
                                    checked={overBasisAccepted}
                                    onChange={(e) =>
                                        setAcceptedAmountCents(
                                            e.target.checked
                                                ? (check?.amountNetCents ?? null)
                                                : null,
                                        )
                                    }
                                />
                                <span>Ja, der Abschlag ist so gewollt.</span>
                            </label>
                        </div>
                    ) : (
                        <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-snug text-emerald-900">
                            Der Abschlag fordert{' '}
                            <strong className="tabular-nums">
                                {formatCents(check?.amountNetCents ?? 0)}
                            </strong>{' '}
                            netto.
                        </p>
                    )}
                </>
            )}
        </PickerStepFrame>
    );
};

/**
 * Schritt „Abschläge abziehen" (Teilleistungsrechnung).
 *
 * WARUM ueberhaupt gefragt statt angehakt: der Haken lag frueher in der linken
 * Spalte und wurde in localStorage gemerkt. Ein aus einer anderen Sitzung
 * mitgebrachtes „an" erzeugte einen Abzugsblock, den niemand verlangt und
 * niemand gesehen hat — derselbe Fehlertyp wie das vererbte „kumulativ".
 *
 * WARUM nur manchmal: gefragt wird nur, wenn das Projekt ueberhaupt
 * abzugsfaehige Abschlaege hat (invoicePickerFlow.ts). Eine Frage, deren beide
 * Antworten dasselbe bewirken, ist keine Frage, sondern ein Klick.
 */
export const DeductAdvancesStep: React.FC<{
    projectName: string;
    step: { position: number; total: number };
    /** Wie viele abzugsfaehige Abschlaege das Projekt hat. */
    count: number;
    onBack?: () => void;
    onCancel: () => void;
    onChoose: (deduct: boolean) => void;
}> = ({ projectName, step, count, onBack, onCancel, onChoose }) => (
    <PickerStepFrame
        projectName={projectName}
        title="Sollen die Abschläge abgezogen werden?"
        step={step}
        onBack={onBack}
        onCancel={onCancel}
    >
        <p className="text-sm leading-snug text-slate-600">
            Für dieses Projekt {count === 1 ? 'ist' : 'sind'}{' '}
            <strong>
                {count} {count === 1 ? 'Abschlagsrechnung' : 'Abschlagsrechnungen'}
            </strong>{' '}
            ausgestellt und noch nicht verbraucht.
        </p>

        <div className="mt-3 space-y-2">
            <PickerChoiceCard title="Abschläge abziehen" onClick={() => onChoose(true)}>
                Rechtlich eine Teilleistungsrechnung: eine gewöhnliche Rechnung über eine erbrachte
                Teilleistung, von der die bisher gestellten Abschläge je Steuersatz vom Zahlbetrag
                abgezogen werden. Die Positionen selbst bleiben unverändert; ausgewiesener Umsatz
                und Umsatzsteuer bleiben die der vollen Leistung — sonst wäre der Vorsteuerabzug des
                Kunden falsch. Abgezogene Abschläge sind danach verbraucht und erscheinen auf keinem
                weiteren Beleg.
            </PickerChoiceCard>
            <PickerChoiceCard title="Ohne Abzug" onClick={() => onChoose(false)}>
                Die gewöhnliche Rechnung über die neuen Mengen. Die Abschläge bleiben stehen und
                werden später auf der Schlussrechnung abgezogen.
            </PickerChoiceCard>
        </div>
    </PickerStepFrame>
);
